import { useEffect, useMemo, useRef, useState } from 'react';
import { GrafoEngine } from './GrafoEngine';
import type { Filtri } from './GrafoEngine';
import { NODI, ARCHI, COLORI, NOMI_TIPO, NOME_CORPUS } from './data/grafo';
import type { TipoNodo } from './data/grafo';

const TIPI: { id: TipoNodo; label: string }[] = [
  { id: 'corpus', label: 'Corpus' },
  { id: 'ue', label: 'Fonte UE' },
  { id: 'naz', label: 'Fonte nazionale' },
  { id: 'reg', label: 'Fonte regionale' },
  { id: 'prassi', label: 'Linee guida e prassi' },
  { id: 'atto', label: 'Atto prodotto' },
];

const ARCHI_LEGENDA: { rel: string; label: string; tratteggio?: boolean; colore: string }[] = [
  { rel: 'contiene', label: 'contiene', colore: '#2b2a72' },
  { rel: 'attua', label: 'attua', colore: '#0b5fbf' },
  { rel: 'cita', label: 'cita', colore: '#9aa0ad' },
  { rel: 'abroga', label: 'abroga', colore: '#c0392b', tratteggio: true },
];

export function GrafoCorpus() {
  const tela = useRef<HTMLCanvasElement | null>(null);
  const motore = useRef<GrafoEngine | null>(null);

  const [corpus, setCorpus] = useState('tutti');
  const [tipi, setTipi] = useState<Record<TipoNodo, boolean>>({
    corpus: true, ue: true, naz: true, reg: true, prassi: true, atto: true,
  });
  const [abrogate, setAbrogate] = useState(false);
  const [sel, setSel] = useState<string | null>(null);

  const filtri: Filtri = useMemo(() => ({ corpus, tipi, abrogate }), [corpus, tipi, abrogate]);

  // Il motore vive quanto la vista. La vista è montata dentro un ramo
  // condizionale: creare il motore fuori da questo effetto lo lascerebbe
  // agganciato a un canvas rimosso dal DOM.
  useEffect(() => {
    if (!tela.current) return;
    const m = new GrafoEngine(tela.current, filtri);
    m.onSelezione = setSel;
    m.avvia();
    motore.current = m;
    return () => { m.ferma(); motore.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { motore.current?.aggiornaFiltri(filtri); }, [filtri]);
  useEffect(() => { motore.current?.seleziona(sel); }, [sel]);

  const visibili = useMemo(
    () => NODI.filter((n) => tipi[n.tipo] && (abrogate || n.vigente) && (corpus === 'tutti' || n.corpus.includes(corpus))),
    [tipi, abrogate, corpus],
  );
  const ids = useMemo(() => new Set(visibili.map((n) => n.id)), [visibili]);
  const nArchi = useMemo(() => ARCHI.filter(([a, b]) => ids.has(a) && ids.has(b)).length, [ids]);

  const conta = (t: TipoNodo) => visibili.filter((n) => n.tipo === t).length;
  const max = Math.max(1, ...TIPI.map((t) => conta(t.id)));

  const nodo = sel ? NODI.find((n) => n.id === sel) : undefined;
  const vicini = useMemo(() => {
    if (!nodo) return [] as { label: string; rel: string; colore: string }[];
    const m = new Map<string, string>();
    for (const [a, b, rel] of ARCHI) {
      if (a === nodo.id) m.set(b, rel);
      else if (b === nodo.id) {
        m.set(a, rel === 'contiene' ? 'contenuto in' : rel === 'cita' ? 'citato da' : rel === 'abroga' ? 'abrogato da' : 'attuato da');
      }
    }
    return [...m.entries()].map(([id, rel]) => {
      const n = NODI.find((x) => x.id === id);
      return { label: n ? n.label : id, rel, colore: n ? COLORI[n.tipo] : '#9aa0ad' };
    });
  }, [nodo]);

  return (
    <>
      <h1>Grafo del corpus</h1>
      <p className="lede">
        Ogni nodo è una fonte, un corpus o un atto prodotto. Gli archi sono le relazioni con cui il RAG
        costruisce una risposta: <strong>contiene</strong>, <strong>attua</strong>, <strong>cita</strong>,{' '}
        <strong>abroga</strong>. Trascina i nodi, scorri per ingrandire, clicca per il dettaglio.
      </p>

      <div className="uc-grafo-riga">
        <div className="uc-grafo-filtri">
          <div className="uc-card">
            <h2>Corpus</h2>
            <select className="uc-sel" value={corpus} onChange={(e) => setCorpus(e.target.value)}>
              <option value="tutti">Tutti i corpora</option>
              <option value="fesr">FESR</option>
              <option value="fse">FSE+</option>
              <option value="feasr">FEASR</option>
            </select>

            <h2 style={{ marginTop: 16 }}>Tipo di nodo</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {TIPI.map((t) => (
                <label className="uc-check" key={t.id}>
                  {/* checked LEGGE lo stato: una checkbox che lo afferma si
                      disallinea dal filtro (bug vero, già corretto). */}
                  <input
                    type="checkbox"
                    checked={tipi[t.id]}
                    onChange={(e) => setTipi({ ...tipi, [t.id]: e.target.checked })}
                    style={{ accentColor: COLORI[t.id] }}
                  />
                  <span className="uc-legenda-punto" style={{ background: COLORI[t.id] }} aria-hidden="true" />
                  {t.label}
                </label>
              ))}
            </div>

            <h2 style={{ marginTop: 16 }}>Vigenza</h2>
            <label className="uc-check" style={{ alignItems: 'flex-start' }}>
              <input type="checkbox" checked={abrogate} onChange={(e) => setAbrogate(e.target.checked)} style={{ marginTop: 2, accentColor: 'var(--uc-rosso)' }} />
              <span>
                Mostra le norme abrogate
                <span style={{ display: 'block', fontSize: 12, color: 'var(--uc-ink-3)', lineHeight: 1.4 }}>
                  Restano nel corpus ma non sono citabili come base giuridica.
                </span>
              </span>
            </label>

            <h2 style={{ marginTop: 16 }}>Archi</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: 'var(--uc-ink-2)' }}>
              {ARCHI_LEGENDA.map((a) => (
                <span key={a.rel} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ flex: 'none', width: 22, height: a.tratteggio ? 0 : 2, background: a.tratteggio ? 'none' : a.colore, borderTop: a.tratteggio ? '2px dashed ' + a.colore : undefined }} />
                  {a.label}
                </span>
              ))}
            </div>
          </div>

          <div className="uc-card">
            <h2 style={{ marginBottom: 4 }}>Nodi visibili</h2>
            <p style={{ fontSize: 12, color: 'var(--uc-ink-3)', margin: '0 0 12px' }}>Si aggiorna con i filtri.</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 13 }}>
              <span style={{ fontSize: 29, fontWeight: 700, letterSpacing: '-.02em' }}>{visibili.length}</span>
              <span style={{ fontSize: 12.5, color: 'var(--uc-ink-3)' }}>nodi · {nArchi} archi</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {TIPI.map((t) => (
                <div key={t.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                    <span>{t.label}</span>
                    <span className="mono" style={{ fontWeight: 500 }}>{conta(t.id)}</span>
                  </div>
                  <div className="uc-barra">
                    <div style={{ width: Math.round((conta(t.id) / max) * 100) + '%', background: COLORI[t.id] }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="uc-grafo-tela">
          <div className="uc-tela-barra">
            <span style={{ fontSize: 12.5, color: 'var(--uc-ink-3)' }}>
              Snapshot <strong className="mono" style={{ color: 'var(--uc-ink)' }}>FESR v12 · FSE+ v8 · FEASR v4</strong>
            </span>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button type="button" className="uc-btn secondario" style={{ minHeight: 34 }} onClick={() => motore.current?.riassesta()}>Riassesta</button>
              <button type="button" className="uc-btn secondario" style={{ minHeight: 34, width: 34, padding: 0 }} aria-label="Riduci" onClick={() => motore.current?.zoom(1 / 1.2)}>−</button>
              <button type="button" className="uc-btn secondario" style={{ minHeight: 34, width: 34, padding: 0 }} aria-label="Ingrandisci" onClick={() => motore.current?.zoom(1.2)}>+</button>
            </div>
          </div>
          <div className="uc-tela-wrap">
            <canvas ref={tela} role="img" aria-label={'Grafo del corpus: ' + visibili.length + ' nodi, ' + nArchi + ' archi'} />
          </div>
        </div>

        <div className="uc-grafo-dett">
          {!nodo ? (
            <>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Dettaglio del nodo</h2>
              <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--uc-ink-3)', margin: 0 }}>
                Clicca un nodo per vederne la scheda: tipo, vigenza, quanti chunk indicizzati, quante volte
                è stato citato negli atti e a cosa è collegato.
              </p>
            </>
          ) : (
            <>
              <div style={{ font: '700 11.5px inherit', letterSpacing: '.11em', color: 'var(--uc-ink-3)' }}>{NOMI_TIPO[nodo.tipo]}</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: '7px 0 0', letterSpacing: '-.008em' }}>{nodo.label}</h2>
              <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--uc-ink-3)', margin: '7px 0 0' }}>{nodo.desc}</p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 12 }}>
                <span className="uc-pill" style={{ background: '#f4f2ee', border: '1px solid #e2ded5', color: 'var(--uc-ink-2)' }}>
                  {nodo.vigente ? 'Vigente' : 'Abrogata — non citabile'}
                </span>
                <span className="uc-pill" style={{ background: '#f4f2ee', border: '1px solid #e2ded5', color: 'var(--uc-ink-2)' }}>
                  {nodo.corpus.map((c) => NOME_CORPUS[c]).join(' · ')}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 16, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--uc-linea-2)' }}>
                <div>
                  <div style={{ fontSize: 11.5, color: 'var(--uc-ink-3)' }}>chunk indicizzati</div>
                  <div className="mono" style={{ fontSize: 19, fontWeight: 700 }}>{nodo.chunk.toLocaleString('it-IT')}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11.5, color: 'var(--uc-ink-3)' }}>citato in atti</div>
                  <div className="mono" style={{ fontSize: 19, fontWeight: 700 }}>{nodo.cit}</div>
                </div>
              </div>

              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--uc-linea-2)' }}>
                <div style={{ font: '700 11.5px inherit', letterSpacing: '.11em', color: 'var(--uc-ink-3)', marginBottom: 10 }}>
                  COLLEGATO A ({vicini.length})
                </div>
                {vicini.map((v) => (
                  <div key={v.label} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: '1px solid #f6f3ed', fontSize: 13 }}>
                    <span style={{ flex: 'none', width: 9, height: 9, borderRadius: '50%', background: v.colore }} aria-hidden="true" />
                    <span style={{ flex: 1, minWidth: 0 }}>{v.label}</span>
                    <span style={{ fontSize: 11.5, color: 'var(--uc-ink-3)' }}>{v.rel}</span>
                  </div>
                ))}
              </div>

              <button type="button" className="uc-btn secondario" style={{ width: '100%', marginTop: 16, minHeight: 40 }} onClick={() => setSel(null)}>
                Chiudi il dettaglio
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
